import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { AppEntryLink } from "@/components/navigation/AppEntryLink";

import { PageHero } from "@/components/hero/PageHero";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { usePricingCopy } from "@/lib/pricingContext";
import {
  DENIAL_MAX_MB,
  MEDICAL_RECORD_MAX_MB,
  MEDICAL_RECORD_MAX_PAGES,
} from "@/lib/trustCopy";

interface Topic {
  title: string;
  body: string;
}

function topics(paidPriceLabel: string): Topic[] {
  return [
  {
    title: "Starting a session",
    body: "Click Begin in the top-right, or Read my denial on the home page. The session is created in your browser; nothing is uploaded yet. The first message from the assistant explains what to do next.",
  },
  {
    title: "Uploading a denial",
    body: `Drop your denial letter on the upload area in the session's Overview tab, or use the "Upload my denial letter" button in the guided panel. PDF, JPG, or PNG up to ${DENIAL_MAX_MB} MB — a clear phone photo works. We extract the text and the assistant will summarise what it found.`,
  },
  {
    title: "Adding a medical record",
    body: `After the denial is processed, the assistant will offer to ingest your medical record. PDF only at launch, up to ${MEDICAL_RECORD_MAX_MB} MB / ${MEDICAL_RECORD_MAX_PAGES} pages — upload the key sections (H&P, discharge summary, relevant consults), not a full chart export. This is optional but strengthens the appeal.`,
  },
  {
    title: "Paying to draft",
    body: `When the paywall banner appears, click Unlock for ${paidPriceLabel}. You’ll be redirected to Stripe checkout and back. Your session resumes automatically with the full chat and analysis intact.`,
  },
  {
    title: "Refining the appeal",
    body: 'Each section has a Refine option. Type a short instruction ("make this more clinical", "remove the third paragraph") and the assistant will rewrite that section only.',
  },
  {
    title: "Exporting",
    body: "Use the Export buttons to download a PDF or DOCX. Both formats are mailable.",
  },
  {
    title: "Refreshing or closing the tab",
    body: "Refreshing or closing your tab starts a new session — earlier work is no longer reachable from your browser, and anything on our servers is deleted by the 24-hour retention sweep. The one exception is the Stripe checkout redirect, which preserves state so the agent can resume drafting after you pay.",
  },
  {
    title: "Something went wrong",
    body: "If the assistant gets stuck, click New case in the session header (or Begin in the top-right) to start a fresh session. If the problem persists, copy any error message you see and contact support.",
  },
  ];
}

export function HelpCenter() {
  const { paidPriceLabel } = usePricingCopy();
  const helpTopics = topics(paidPriceLabel);
  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <Header />
      <main className="flex-1">
        <PageHero
          eyebrow="Help center"
          title="How to use Revetra."
          subtitle="Short answers to the most common questions while you’re working through an appeal."
        />
        <section className="bg-bg py-20">
          <div className="wrap max-w-3xl">
            <div className="space-y-3">
              {helpTopics.map((t) => (
                <details
                  key={t.title}
                  className="rounded-xl border border-border bg-surface p-5 transition hover:border-primary/30 [&_summary::-webkit-details-marker]:hidden"
                >
                  <summary className="cursor-pointer font-heading text-base font-bold tracking-tight text-ink">
                    {t.title}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted">{t.body}</p>
                </details>
              ))}
            </div>
            <div className="mt-12 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <AppEntryLink to="/session" className="btn-indigo inline-flex items-center gap-2 px-5 py-3">
                <span>Start a session</span>
                <ArrowRight size={16} aria-hidden />
              </AppEntryLink>
              <Link to="/faq" className="btn-glass px-5 py-3">
                See FAQ
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
